import {
  byEnvironment,
  EnvironmentMapperFn,
  ENVIRONMENTS,
} from './environments';

// Prestage is a stage-flavored dev environment: its own CDN host, but it hits the
// same STAGE APIs as stage at runtime. So the four service URLs below mirror stage,
// while getAppUrl / getPopupUrl have their own prestage host.
// Preprod is a dev-only CDN that shares stage AWS infra but talks to prod
// APIs at runtime. For the four service URLs below, preprod returns the same
// value as prod because that's the whole point of preprod (run against real
// IMS). For the two CDN URLs (getAppUrl, getPopupUrl), preprod has its own
// host because that's where the preprod-deployed code physically lives.
export function getApiUrl(getEnvironment: EnvironmentMapperFn) {
  return () =>
    byEnvironment(getEnvironment)<string>({
      [ENVIRONMENTS.local]: () => 'https://ims-na1-stg1.adobelogin.com',
      [ENVIRONMENTS.stage]: () => 'https://ims-na1-stg1.adobelogin.com',
      [ENVIRONMENTS.prestage]: () => 'https://ims-na1-stg1.adobelogin.com',
      [ENVIRONMENTS.preprod]: () => 'https://ims-na1.adobelogin.com',
      [ENVIRONMENTS.prod]: () => 'https://ims-na1.adobelogin.com',
    });
}

export function getFederatedUrl(getEnvironment: EnvironmentMapperFn) {
  return () =>
    byEnvironment(getEnvironment)<string>({
      [ENVIRONMENTS.local]: () =>
        'https://federatedid-na1-stg1.services.adobe.com',
      [ENVIRONMENTS.stage]: () =>
        'https://federatedid-na1-stg1.services.adobe.com',
      [ENVIRONMENTS.prestage]: () =>
        'https://federatedid-na1-stg1.services.adobe.com',
      [ENVIRONMENTS.preprod]: () =>
        'https://federatedid-na1.services.adobe.com',
      [ENVIRONMENTS.prod]: () => 'https://federatedid-na1.services.adobe.com',
    });
}

export function getAuthServicesUrl(getEnvironment: EnvironmentMapperFn) {
  return () =>
    byEnvironment(getEnvironment)<string>({
      [ENVIRONMENTS.local]: () => 'http://localhost.corp.adobe.com:3000',
      [ENVIRONMENTS.stage]: () => 'https://auth-stg1.services.adobe.com',
      [ENVIRONMENTS.prestage]: () => 'https://auth-stg1.services.adobe.com',
      [ENVIRONMENTS.preprod]: () => 'https://auth.services.adobe.com',
      [ENVIRONMENTS.prod]: () => 'https://auth.services.adobe.com',
    });
}

export function getAdobeidServicesUrl(getEnvironment: EnvironmentMapperFn) {
  return () =>
    byEnvironment(getEnvironment)<string>({
      [ENVIRONMENTS.local]: () => 'http://localhost.corp.adobe.com:3000',
      [ENVIRONMENTS.stage]: () =>
        ' https://adobeid-na1-stg1.services.adobe.com',
      [ENVIRONMENTS.prestage]: () =>
        'https://adobeid-na1-stg1.services.adobe.com',
      [ENVIRONMENTS.preprod]: () => 'https://adobeid-na1.services.adobe.com',
      [ENVIRONMENTS.prod]: () => 'https://adobeid-na1.services.adobe.com',
    });
}

export function getAppUrl(getEnvironment: EnvironmentMapperFn) {
  return () =>
    byEnvironment(getEnvironment)<string>({
      [ENVIRONMENTS.local]: () => 'https://localhost.corp.adobe.com:25000',
      [ENVIRONMENTS.stage]: () => 'https://auth-light.identity-stage.adobe.com',
      [ENVIRONMENTS.prestage]: () =>
        'https://auth-light.identity-prestage.adobe.com',
      [ENVIRONMENTS.preprod]: () =>
        'https://auth-light.identity-preprod.adobe.com',
      [ENVIRONMENTS.prod]: () => 'https://auth-light.identity.adobe.com',
    });
}

export function getPopupUrl(getEnvironment: EnvironmentMapperFn) {
  return () =>
    byEnvironment(getEnvironment)<string>({
      [ENVIRONMENTS.local]: () =>
        'https://localhost.corp.adobe.com:25000/wrapper-popup-helper/index.html',
      [ENVIRONMENTS.stage]: () =>
        'https://auth-light.identity-stage.adobe.com/wrapper-popup-helper/index.html',
      [ENVIRONMENTS.prestage]: () =>
        'https://auth-light.identity-prestage.adobe.com/wrapper-popup-helper/index.html',
      [ENVIRONMENTS.preprod]: () =>
        'https://auth-light.identity-preprod.adobe.com/wrapper-popup-helper/index.html',
      [ENVIRONMENTS.prod]: () =>
        'https://auth-light.identity.adobe.com/wrapper-popup-helper/index.html',
    });
}

export const sentryUrl = (
  getEnvironment: EnvironmentMapperFn,
  forceStage = false
) =>
  function sentryUrl(variant: string) {
    const envUrl = getAppUrl(
      forceStage ? () => ENVIRONMENTS.stage : getEnvironment
    )();
    const url = new URL(envUrl || '');
    url.hash = variant;

    return url;
  };

export function create(getEnvironment: EnvironmentMapperFn) {
  return {
    getApiUrl: getApiUrl(getEnvironment),
    getAppUrl: getAppUrl(getEnvironment),
    getFederatedUrl: getFederatedUrl(getEnvironment),
    getAuthServicesUrl: getAuthServicesUrl(getEnvironment),
    getAdobeidServicesUrl: getAdobeidServicesUrl(getEnvironment),
    getPopupUrl: getPopupUrl(getEnvironment),
    sentryUrl: sentryUrl(getEnvironment),
  };
}
